/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable camelcase */
import React, { useState } from "react";
import { ArrowRightShape, JobSearchHit, JobSearchLocation } from "@themuse/design-system";
import { triggerSnowplowClickEvent } from "@themuse/snowplow-js-client";
import { UserInteractionActionTypes } from "interfaces/context/searchContextInterfaces";
import { genJobTrackingProps, TrackedElement } from "utils/tracking";
import { useSearchState, useSearchDispatch } from "hooks/SearchContext";
import { NullLogo } from "components/icons/NullLogo";
import { pushUrl } from "utils/strings";
import styles from "./JobTileMinimal.module.scss";

export interface GetJobTileLocationProps {
    inputDMA: string;
    remote_work_location_type: string;
    locations: JobSearchLocation[];
    userLocationDma: string;
}

/**
 * Attempt to pull the closest matching location out of the job.
 */
export const getJobTileLocation = (props: GetJobTileLocationProps): string[] => {
    const {
        inputDMA,
        remote_work_location_type,
        locations,
        userLocationDma
    } = props;
    const isRemote: boolean = remote_work_location_type === "virtual";
    // We always prefer the dropdown if it was used.
    const userLocation: string = inputDMA || userLocationDma;
    // We Only care about the actual location address and fallback to country.
    // eslint-disable-next-line max-len
    const locationStrings: string[] = locations.map((x) => (!(/\w/).test(x.address) ? (x.country as undefined as string).toUpperCase() : x.address));
    // This is the fully matched location (city and state).
    const matchedLocation: string = locationStrings.find((loc) => loc === userLocation);
    // We may need to match only on the last segment of the location.
    const getState = (txt: string) => {
        if (typeof txt === "string") {
            return txt.split(",")[1]?.trim()?.toLowerCase();
        }
        return null;
    };
    // First location matched by region.
    const matchedRegion: string = locationStrings.find((address) => getState(address) === getState(userLocation));
    // First location in the list (fall back)
    const firstLocation: string = locationStrings[0];
    // The location to display
    const displayLocation: string = matchedLocation || matchedRegion || firstLocation || "";
    // If we have more than 2 locations pluralize the string.
    const locationWord: string = ` location${locationStrings.length > 2 ? "s" : ""}`;
    // If we have more than 1 location we add a plus sign and the number plus the word location(s)
    const extraLocations: string = locationStrings.length > 1 ? ` + ${locationStrings.length - 1}${locationWord}` : "";
    // If we are remote and there is a location we will separate them with a slash.
    const separator: string = displayLocation ? " / " : "";
    // Build the remote string.
    const remoteString: string = isRemote ? `${separator}Remote` : "";

    // all together now...
    return [`${displayLocation}${remoteString}${extraLocations}`];
};

interface GetTrackingProps {
    job: JobSearchHit;
    viewJobHref: string;
    total: number;
    index: number;
    searchId: string;
}

/**
 * Generates the tracking props for a card.
 * Returns an object.
 */
export const getTrackingProps = (props: GetTrackingProps): Record<string, any> => {
    const {
        job,
        viewJobHref,
        total,
        index,
        searchId
    } = props;
    return {
        ...genJobTrackingProps(job),
        link_href: viewJobHref,
        total_results_on_page: total,
        placement: "search_results",
        order_num: index + 1,
        sp_tile_order: index + 1,
        sp_tile_search_id: searchId,
        sp_impression_search_id: searchId,
    };
};

interface EventData {
    searchId: string;
    companyLegacyId: string;
    legacy_id: string;
    snowplow_id: string;
    index: number;
}

/**
 * Generates Snowplow event base data for the various job tile click events.
 */
export const getSnowplowEventData = (eventData: EventData) => {
    const {
        searchId,
        companyLegacyId,
        legacy_id,
        snowplow_id,
        index,
    } = eventData;
    return {
        sp_click_component: "button",
        sp_click_component_tile_type: "job",
        sp_click_button_type: null,
        sp_click_position: "main",
        sp_click_target_page_section: "job_detail",
        sp_click_search_id: searchId,
        sp_tile_type: "job",
        sp_tile_company_id: companyLegacyId?.toString() ?? "",
        sp_tile_entity_id: legacy_id?.toString() ?? "",
        sp_tile_entity_uuid: snowplow_id,
        sp_tile_location: null,
        sp_tile_order: index + 1
    };
};

interface JobTileProps {
    job: JobSearchHit;
    index: number;
    total: number;
    inputDMA: string;
    userLocationDma: string;
    viewed?: boolean;
    selectedJob: string | null;
    setSelectedJob: React.Dispatch<React.SetStateAction<string>>;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * JobTileMinimal is similar to the JobTile but has fewer buttons and very different triggers.
 */
export const JobTileMinimal: React.FC<JobTileProps> = ({
    job,
    total,
    inputDMA,
    userLocationDma,
    viewed,
    index,
    selectedJob,
    setSelectedJob,
    setIsOpen,
}) => {
    const searchDispatch = useSearchDispatch();
    const { searchId } = useSearchState();
    const {
        company: {
            legacy_id: companyLegacyId,
            logo,
            name,
            short_name,
            pkg,
            client_tier
        },
        id,
        legacy_id,
        locations,
        postedDate,
        remote_work_location_type,
        short_title,
        snowplow_id,
        title,
    } = job.hit as undefined as Record<string, any>;
    const [imageError, setImageError] = useState<boolean>(false);

    const companyHref = `/profiles/${short_name}`;
    const viewJobHref = `/jobs/${short_name}/${short_title}`;
    const ctaLabel = `View ${title}. This opens in a new tab.`;

    // a list of rules for using the fallback logo
    const logoRules = [
        // Some payment tiers do not have logos or profiles.
        ["archived", "lite"].includes(client_tier),
        pkg === null,
        // Sometimes we have not yet received the companies logo.
        !logo,
        // An error loading the logo.
        imageError
    ];
    const logoImage = logoRules.some(Boolean) ? null : logo;

    /**
     * Generates the tracking properties for a job tile.
     */
    const trackingProps = getTrackingProps({
        job, viewJobHref, total, index, searchId
    });

    /**
     * Generates the location of a job tile based on various location parameters.
     */
    const tileLocation = getJobTileLocation({
        inputDMA,
        remote_work_location_type,
        locations,
        userLocationDma
    });

    /**
     * Generates the Snowplow event data for tracking purposes.
     */
    const eventData = getSnowplowEventData({
        searchId,
        companyLegacyId,
        legacy_id,
        snowplow_id: snowplow_id || id,
        index,
    });

    /**
     * Handles the click event on the entire job tile.
     *
     * Stops the event from propagating and prevents the default action.
     * Triggers a Snowplow click event with specific tracking data.
     * Sets the active job.
     * Shows the job description iframe.
     */
    const handleTileClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        triggerSnowplowClickEvent({
            ...eventData,
            sp_click_component: "tile"
        });
        const jobPath = `${job.hit.company.short_name}/${job.hit.short_title}`;
        const params = new URLSearchParams(window.location.search);
        const jobParam = params.get("job") || "1";
        const page = (window.location.pathname).startsWith("/search") ? 1 : parseInt(jobParam, 10);
        setSelectedJob(jobPath);
        pushUrl(page, window.location.pathname, jobPath);
        setIsOpen(true);
    };

    /**
     * Handles the click event on a view job link within a job tile.
     *
     * Stops the event from propagating and prevents the default action.
     * Triggers a Snowplow click event with specific tracking data.
     * Opens the job description page in a new browser tab.
     */
    const handleViewClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        triggerSnowplowClickEvent({
            ...eventData,
            sp_click_component: "button",
            sp_click_button_type: "job_view",
            sp_click_button_text: "View Job >",
        });
        window.open(viewJobHref, "_blank");
    };

    /**
     * Handles the click event on a company name link within a job tile.
     *
     * Stops the event from propagating and prevents the default action.
     * Triggers a Snowplow click event with specific tracking data.
     * Opens the company's profile page in a new browser tab.
     */
    const handleCompanyClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        triggerSnowplowClickEvent({
            ...eventData,
            sp_click_component: "tile",
            sp_click_button_type: "profile",
            sp_click_button_text: name,
            sp_click_target_page_section: "profile",
        });
        window.open(companyHref, "_blank");
    };
    const isSelectedJob = selectedJob?.includes(`${job.hit.company.short_name}/${job.hit.short_title}`);
    return (
        <TrackedElement
            key={`tracked-${id}`}
            eventName="Job Tile Viewed"
            eventProps={trackingProps}
            enabled={!viewed}
            callback={() => searchDispatch({
                type: UserInteractionActionTypes.updateJobTileViews,
                payload: { tilesViewedJobIds: [id] }
            })}
        >
            <div
                className={`${styles.jobTile} ${isSelectedJob ? styles.selected : ""}`}
                onClick={handleTileClick}
                role="presentation"
            >
                <div className={styles.logo}>
                    {logoImage
                        ? (
                            <img
                                src={logoImage}
                                alt=""
                                width="100"
                                height="100"
                                loading="eager"
                                onError={() => setImageError(true)}
                            />
                        )
                        : (<NullLogo />)}
                </div>
                <div className={styles.body}>
                    <h2 className={styles.title}>{title}</h2>
                    <div className={styles.details}>
                        At
                        {" "}
                        {(!pkg || pkg === "Programmatic") ? (
                            <>{name}</>
                        ) : (
                            <a
                                href={companyHref}
                                onClick={handleCompanyClick}
                                onContextMenuCapture={handleCompanyClick}
                            >
                                {name}
                            </a>
                        )}
                        {" "}
                        -
                        {tileLocation}
                    </div>
                    <div className={styles.details}>
                        Posted on
                        {" "}
                        {postedDate}
                    </div>
                    <a
                        className={styles.cta}
                        href={viewJobHref}
                        aria-label={ctaLabel}
                        onClick={handleViewClick}
                        onContextMenuCapture={handleViewClick}
                    >
                        <span>View Job</span>
                        <ArrowRightShape />
                    </a>
                </div>
                <div>{" "}</div>
            </div>
        </TrackedElement>
    );
};
JobTileMinimal.displayName = "JobTileMinimal";

export default JobTileMinimal;
